import { HTMLAttributes } from 'react';
import { useCustomNavigate } from '@/website/hooks/useCustomNavigate';
import { Routes } from '@/website/util/routes';
import { JoveButton } from '@jove-frontend/mui';
import { openUrlWithNewWindow, useJoveBreakpoint } from '@jove-frontend/uikit';
import { Box, Typography } from '@mui/material';
import clsx from 'clsx';

type BrokerProductIntroductionViewProps = HTMLAttributes<HTMLDivElement> & {
  info: {
    introductionTitle: string;
    introductionDescription: string;
    introductionDescriptionList: string[];
    introductionRelatedCompanyList: string[];
    introductionImage: string;
    introductionMarkerTitle: string;
    introductionBookMeetingUrl: string;
    bookMeetingButtonText: string;
    activateBrokerAccessButtonText: string;
  };
};

const ImageView = (props: BrokerProductIntroductionViewProps) => {
  const { info } = props;
  const { isMobileLayout } = useJoveBreakpoint();
  return (
    <Box className={clsx('relative', isMobileLayout ? 'flex h-auto' : 'min-h-[380px] w-0 max-w-[532px] flex-shrink-0 flex-grow')}>
      <img src={info?.introductionImage} alt="Image" className={clsx('h-full w-auto object-cover', isMobileLayout ? '' : 'rounded-3xl')} />
    </Box>
  );
};

const BrokerProductIntroductionView = (props: BrokerProductIntroductionViewProps) => {
  const { className, info } = props;
  const { isMobileLayout } = useJoveBreakpoint();
  const { push } = useCustomNavigate();

  /**
   * methods
   */
  const bookMeetingAction = () => {
    if (info?.introductionBookMeetingUrl) {
      openUrlWithNewWindow(info.introductionBookMeetingUrl);
    }
  };

  const goToBrokerOnboardingAction = () => {
    push({ path: Routes.brokerOnboardingSubmit() });
  };

  return (
    <Box className={clsx('flex flex-col items-center bg-cover', className)}>
      <Box className={clsx('flex items-center', isMobileLayout ? 'w-full flex-col space-y-8 pt-4' : 'w-[1180px] flex-row space-x-16')}>
        {isMobileLayout && <ImageView info={info} />}
        <Box className={clsx('flex flex-1 flex-col space-y-8', isMobileLayout ? 'mt-4 px-2' : 'my-16')}>
          <Box className="flex flex-row self-start rounded-full bg-white px-3 py-2">
            <Typography variant="subtitle1" className="text-brand-secondary font-extrabold">
              {info?.introductionMarkerTitle}
            </Typography>
          </Box>
          <Typography variant="h3" className="text-primary-black font-serif text-5xl font-black">
            {info?.introductionTitle}
          </Typography>
          <Box className="flex flex-col">
            {(info?.introductionDescriptionList || []).map((item, index) => {
              return (
                <Typography key={index} variant="h6" className="text-primary-black font-medium leading-7">
                  {item}
                </Typography>
              );
            })}
          </Box>
          <Box className="flex flex-row flex-wrap gap-x-6 gap-y-2">
            {(info?.introductionRelatedCompanyList || []).map((item, index) => {
              return <img key={index} src={item} alt="Recruitment introduction" className="h-10 w-auto object-cover" />;
            })}
          </Box>
          <Box className={clsx('flex', isMobileLayout ? 'flex-col space-y-2' : 'flex-row space-x-5')}>
            <JoveButton size="large" onClick={bookMeetingAction}>
              {info?.bookMeetingButtonText}
            </JoveButton>
            <JoveButton size="large" variant="contained" onClick={goToBrokerOnboardingAction}>
              {info?.activateBrokerAccessButtonText}
            </JoveButton>
          </Box>
        </Box>
        {!isMobileLayout && <ImageView info={info} />}
      </Box>
    </Box>
  );
};

export default BrokerProductIntroductionView;
